import React, { useEffect, useState } from 'react'
import { Card, Flex, Switch, Form, Alert, type FormInstance, Checkbox } from 'antd'
import { TreeSelect } from '../ant-overrides/AntTreeSelect'
import AssigneeSelect from '../../../shared/webcomponents/react-wrappers/AssigneeSelect'
import type { AssigneeIds, Team, User } from '../../../shared/domain/schedule/assignee.no-deps'
import type { ReportStatus } from './ReportStatusFlowSettings'
import type { AdvancedSettingsForm, ReportStatusRow } from './ReportStatusRow'
import { AdvancedSettingsCache } from './AdvancedSettingsCacheMixin'
import { Translations } from '../../../shared/i18n/Translations'
import type { Flags } from '../../../shared/cfg/flags.no-deps'
import { InfoPopover } from '../ui/InfoPopover'

interface AdvancedSettingsProps {
    setAnyAdvancedSettingsDirty: (isEditorDirty: boolean) => void
    row: ReportStatusRow
    allUsers: User[]
    allTeams: Team[]
    allStatuses: ReportStatus[]
    isOpen: boolean
    onCancelClicked: (isDirty: boolean) => void
    onSaveClicked: (settings: AdvancedSettingsForm) => void
    openAdvancedSettings: () => void
    // deprecated - can remove once approvalFlowsMultipleFinals flag is on for all customers
    isAnyOtherStatusFinal: boolean
    showValidationErrors: boolean
    featureFlags: Partial<Flags>
}

/**
 * @deprecated We are changing everything to be edited inline so once that flag is on this file can be deleted
 */
export function AdvancedSettings(props: AdvancedSettingsProps) {
    const { cacheFormValues, getCachedFormValues, clearCachedFormValues } = AdvancedSettingsCache(props.row.uuid)
    const [formInstance] = useState<FormInstance<AdvancedSettingsForm>>(Form.useForm().pop())
    const [isEditorDirty, setIsEditorDirty] = useState(false)
    const [noAssigneeSelected, setNoAssigneeSelected] = useState(
        isNoAssigneeSelected(props.row.canBeChangedByAssignees)
    )

    useEffect(() => {
        if (props.isOpen) {
            loadStoredFormValues()
            if (props.showValidationErrors) {
                formInstance.validateFields({ recursive: true })
            }
        } else {
            formInstance.resetFields()
            setIsEditorDirty(false)
            if (!!getCachedFormValues()) {
                props.openAdvancedSettings()
            }
        }
    }, [props.isOpen, props.showValidationErrors])

    const getSettings = () => {
        return formInstance.getFieldsValue()
    }

    if (!props.isOpen) {
        return <></>
    }

    return (
        <Card className="report-status__row-advanced">
            <Flex vertical gap={'32px'}>
                <div>
                    <label className="report-status__row-advanced--name">{props.row.name}</label>
                    {props.row.isDefaultStatus && (
                        <span className="report-status__row--advanced--info-label">
                            {'(' + window.i18n('appcore:reportStatus.default') + ')'}
                        </span>
                    )}
                    {props.row.isFinalStatus && (
                        <span className="report-status__row--advanced--info-label">
                            {'(' + window.i18n('appcore:reportStatus.final') + ')'}
                        </span>
                    )}
                    <Form
                        layout="vertical"
                        form={formInstance}
                        onValuesChange={(changedValues, values) => {
                            const currentValues = {
                                ...values,
                                ...changedValues,
                                isDirty: formInstance.isFieldsTouched(),
                            }
                            cacheFormValues(currentValues)
                            const noAssignee = isNoAssigneeSelected(currentValues.canBeChangedByAssignees)
                            setNoAssigneeSelected(noAssignee)
                            setIsEditorDirty(formInstance.isFieldsTouched())
                            props.setAnyAdvancedSettingsDirty(formInstance.isFieldsTouched())
                            if (noAssignee) {
                                formInstance.setFieldValue('notifyUsersWhenStatusBecomesActive', false)
                            }
                        }}
                    >
                        <h4 className="has-font-weight-bold advanced-settings-title">
                            {window.i18n('appcore:reportStatus.advancedSettingsTitle')}
                        </h4>
                        <Flex gap="32px">
                            <div className="has-fullwidth">
                                <Form.Item
                                    label={
                                        <SelectLabel
                                            labelKey="appcore:reportStatus.canBeChangedToStatusesHeader"
                                            tooltipKey="appcore:reportStatus.statusSelectHelperTooltip"
                                        />
                                    }
                                    help={window.i18n('appcore:reportStatus.anyStatusHelpText')}
                                    name="canBeChangedToStatuses"
                                    initialValue={props.row.canBeChangedToStatuses}
                                >
                                    {/* todo make this regular select */}
                                    <TreeSelect
                                        className="has-fullwidth can-be-changed-to-select"
                                        placeholder={window.i18n('appcore:reportStatus.anyStatus')}
                                        treeData={props.allStatuses
                                            .filter((status) => status.uuid !== props.row.uuid)
                                            .map((status) => ({
                                                label: status.name,
                                                value: status.uuid,
                                            }))}
                                        sortTreeDataAlphabetically={false}
                                        treeIcon={false}
                                    />
                                </Form.Item>
                            </div>
                            <div className="has-fullwidth">
                                <Form.Item
                                    label={
                                        <SelectLabel
                                            labelKey="appcore:reportStatus.canBeChangedByStatusesHeader"
                                            tooltipKey="appcore:reportStatus.assigneeSelectHelperTooltip"
                                        />
                                    }
                                    name="canBeChangedByAssignees"
                                    className="report-status__row--advanced--can-be-changed-by__form-item"
                                    initialValue={props.row.canBeChangedByAssignees}
                                    help={window.i18n('appcore:reportStatus.assigneeSelectHelperText')}
                                    valuePropName="selectedIds"
                                    trigger="onSelectedIdsChanged"
                                    getValueFromEvent={(e: CustomEvent<AssigneeIds>) => e.detail}
                                >
                                    <AssigneeSelect
                                        data-testid="status-row-assignee-select"
                                        placeholder={window.i18n('appcore:reportStatus.anyone')}
                                        users={props.allUsers}
                                        teams={props.allTeams}
                                        language={Translations.language}
                                        multi-select
                                        allow-select-all-teams={false}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="notifyUsersWhenStatusBecomesActive"
                                    valuePropName="checked"
                                    initialValue={!!props.row.notifyUsersWhenStatusBecomesActive}
                                    style={{ marginBottom: '0' }}
                                    hidden={!props.featureFlags['approval-flows-notifications']}
                                >
                                    <Checkbox disabled={noAssigneeSelected}>
                                        {window.i18n('appcore:reportStatus.notifyUsersWhenStatusBecomesActive')}
                                    </Checkbox>
                                </Form.Item>
                            </div>
                        </Flex>
                        <div style={{ display: 'flex' }} className="final-status">
                            <Form.Item
                                help={
                                    props.featureFlags['approval-flows-multiple-finals']
                                        ? null
                                        : window.i18n('appcore:reportStatus.finalStatusToggleInfo')
                                }
                                label={
                                    <SelectLabel
                                        labelKey="appcore:reportStatus.finalStatus"
                                        tooltipKey="appcore:reportStatus.finalStatusTooltip"
                                    />
                                }
                                name={'isFinalStatus'}
                                initialValue={props.row.isFinalStatus}
                                valuePropName="checked"
                                getValueFromEvent={(checked: boolean) => checked}
                            >
                                <Switch
                                    disabled={
                                        props.isAnyOtherStatusFinal &&
                                        !props.featureFlags['approval-flows-multiple-finals']
                                    }
                                />
                            </Form.Item>
                        </div>

                        <Flex className="report-status__row--advanced--button-container" vertical gap={'16px'}>
                            {isEditorDirty && props.showValidationErrors && (
                                <div>
                                    <Alert
                                        description={window.i18n('appcore:reportStatus.finishEditingErrorMessage')}
                                        type="error"
                                        showIcon
                                        icon={<zaptic-icon style={{ fontSize: '2rem' }} icon-name="alert" />}
                                    />
                                </div>
                            )}
                            <div>
                                <zaptic-button
                                    type="secondary"
                                    onClick={() => {
                                        clearCachedFormValues()
                                        props.onCancelClicked(isEditorDirty)
                                    }}
                                >
                                    {window.i18n('appcore:reportStatus.cancel')}
                                </zaptic-button>
                                <zaptic-button
                                    disabled={!isEditorDirty}
                                    onClick={() => {
                                        clearCachedFormValues()
                                        props.onSaveClicked(getSettings())
                                    }}
                                >
                                    {window.i18n('appcore:common.done')}
                                </zaptic-button>
                            </div>
                        </Flex>
                    </Form>
                </div>
            </Flex>
        </Card>
    )

    function loadStoredFormValues() {
        const cachedValues = getCachedFormValues()

        if (cachedValues) {
            formInstance.setFieldsValue(cachedValues)
            setIsEditorDirty(cachedValues.isDirty)
        }
    }
}

type SelectLabelProps = {
    labelKey: string
    tooltipKey: string
}

function SelectLabel({ labelKey, tooltipKey }: SelectLabelProps) {
    return (
        <Flex gap="4px" align="center">
            <span>{window.i18n(labelKey)}</span>
            <InfoPopover title={window.i18n(tooltipKey)} />
        </Flex>
    )
}

function isNoAssigneeSelected({ teams, users }: AssigneeIds) {
    return teams.length + users.length === 0
}
